// src/components/AdminPanel.js (최종 수정된 버전)

import React, { useState, useEffect } from 'react';
import { useWallet } from '../hooks/useWallet';
import { 
  getReadOnlyContract, 
  getContract, 
  weiToWLC, 
  withdrawFees, 
  transferOwnership,
  setFeePercent,
  getCurrentFeePercent,
  getCurrentFeePercentage,
  feePercentToPercentage,
  percentageToFeePercent,
  DEFAULT_FEE_PERCENT
} from '../utils/contract';
import './AdminPanel.css';

const AdminPanel = () => {
  const { account } = useWallet();
  const [contractInfo, setContractInfo] = useState({
    owner: '',
    totalFees: '0',
    contractBalance: '0',
    nextTradeId: '0',
    currentFeePercent: DEFAULT_FEE_PERCENT,
    currentFeePercentage: feePercentToPercentage(DEFAULT_FEE_PERCENT)
  });
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newOwnerAddress, setNewOwnerAddress] = useState('');
  
  // 수수료율 관리 상태
  const [feeSettings, setFeeSettings] = useState({
    newFeePercent: '',
    newFeePercentage: '',
    isChangingFee: false,
    feeChangeProgress: 0,
    feeChangeState: 'idle' // 'idle', 'confirming', 'processing', 'completed', 'error'
  });
  
  // 수수료 출금 관련 상태
  const [withdrawalState, setWithdrawalState] = useState('idle'); // 'idle', 'confirming', 'processing', 'completed', 'error'
  const [withdrawalProgress, setWithdrawalProgress] = useState(0);
  const [withdrawalResult, setWithdrawalResult] = useState(null);

  // 컨트랙트 정보 로드
  const loadContractInfo = async () => {
    try {
      const contract = await getReadOnlyContract();
      
      const [owner, totalFees, contractBalance, nextTradeId, currentFeePercent, currentFeePercentage] = await Promise.all([
        contract.owner(),
        contract.totalFees(),
        contract.getContractBalance(),
        contract.nextTradeId(),
        getCurrentFeePercent(),
        getCurrentFeePercentage()
      ]);

      setContractInfo({
        owner,
        totalFees: weiToWLC(totalFees),
        contractBalance: weiToWLC(contractBalance),
        nextTradeId: nextTradeId.toString(),
        currentFeePercent,
        currentFeePercentage
      });

      // 현재 계정이 소유자인지 확인
      setIsOwner(account && owner.toLowerCase() === account.toLowerCase());

    } catch (error) {
      console.error('컨트랙트 정보 로드 실패:', error);
      setError('컨트랙트 정보를 불러올 수 없습니다.');
    }
  };

  useEffect(() => {
    if (account) {
      loadContractInfo();
    }
  }, [account]);

  // 진행률 애니메이션
  const animateProgress = (targetProgress, duration = 1000) => {
    const startProgress = withdrawalProgress;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentProgress = startProgress + (targetProgress - startProgress) * progress;
      
      setWithdrawalProgress(currentProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // 수수료율 변경 애니메이션
  const animateFeeChangeProgress = (targetProgress, duration = 1000) => {
    const startProgress = feeSettings.feeChangeProgress;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentProgress = startProgress + (targetProgress - startProgress) * progress;
      
      setFeeSettings(prev => ({
        ...prev,
        feeChangeProgress: currentProgress
      }));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };

  // 수수료율 입력 핸들러
  const handleFeePercentageChange = (e) => {
    const percentage = e.target.value;
    const feePercent = percentage ? percentageToFeePercent(percentage) : '';
    
    setFeeSettings(prev => ({
      ...prev,
      newFeePercentage: percentage,
      newFeePercent: feePercent
    }));
  };

  // 수수료율 변경
  const handleChangeFeePercent = async () => {
    if (!isOwner) {
      setError('수수료율 변경 권한이 없습니다.');
      return;
    }

    const newFeePercent = feeSettings.newFeePercent;
    const newPercentage = feeSettings.newFeePercentage;

    if (newFeePercent < 0 || newFeePercent > 1000) {
      setError('올바른 수수료율을 입력해주세요. (0% ~ 10%)');
      return;
    }

    const confirmMessage = `수수료율을 ${newPercentage}%로 변경하시겠습니까?\n\n현재: ${contractInfo.currentFeePercentage}\n변경 후: ${newPercentage}%\n\n⚠️ 주의: 변경 후 생성되는 새 거래부터 적용됩니다.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setFeeSettings(prev => ({
      ...prev,
      isChangingFee: true,
      feeChangeState: 'confirming',
      feeChangeProgress: 0
    }));

    try {
      // 1단계: 사용자 확인 대기
      animateFeeChangeProgress(30, 500);
      
      // 2단계: 트랜잭션 생성
      setFeeSettings(prev => ({
        ...prev,
        feeChangeState: 'processing'
      }));
      animateFeeChangeProgress(60, 800);
      
      const startTime = Date.now();
      
      // 수수료율 변경 실행
      await setFeePercent(newFeePercent);
      
      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000).toFixed(1);
      
      // 3단계: 트랜잭션 완료
      animateFeeChangeProgress(100, 500);
      
      setTimeout(() => {
        setFeeSettings(prev => ({
          ...prev,
          feeChangeState: 'completed',
          newFeePercent: '',
          newFeePercentage: ''
        }));
        
        setSuccess(`수수료율이 ${newPercentage}%로 성공적으로 변경되었습니다! (처리시간: ${duration}초)`);
        
        // 정보 새로고침
        loadContractInfo();
      }, 500);

    } catch (error) {
      console.error('수수료율 변경 실패:', error);
      setFeeSettings(prev => ({
        ...prev,
        feeChangeState: 'error'
      }));
      
      let errorMessage = '수수료율 변경에 실패했습니다.';
      
      if (error.code === 4001) {
        errorMessage = '사용자가 트랜잭션을 거부했습니다.';
      } else if (error.message) {
        errorMessage = '수수료율 변경에 실패했습니다: ' + error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      
      // 5초 후 상태 초기화
      setTimeout(() => {
        setFeeSettings(prev => ({
          ...prev,
          isChangingFee: false,
          feeChangeState: 'idle',
          feeChangeProgress: 0
        }));
        setSuccess(null);
        setError(null);
      }, 10000);
    }
  };

  // 수수료 출금 (향상된 버전)
  const handleWithdrawFees = async () => {
    if (!isOwner) {
      setError('수수료 출금 권한이 없습니다.');
      return;
    }

    if (parseFloat(contractInfo.totalFees) === 0) {
      setError('출금할 수수료가 없습니다.');
      return;
    }

    const feeAmount = contractInfo.totalFees;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    setWithdrawalResult(null);
    setWithdrawalState('confirming');
    setWithdrawalProgress(0);

    try {
      // 1단계: 사용자 확인 대기
      animateProgress(20, 500);
      
      // 2단계: 트랜잭션 생성
      setWithdrawalState('processing');
      animateProgress(40, 800);
      
      const startTime = Date.now();
      
      // contract.js의 withdrawFees 함수 사용
      await withdrawFees();
      
      const endTime = Date.now();
      const duration = ((endTime - startTime) / 1000).toFixed(1);
      
      // 3단계: 트랜잭션 완료
      animateProgress(100, 500);
      
      setTimeout(() => {
        setWithdrawalState('completed');
        
        // 결과 정보 저장
        setWithdrawalResult({
          amount: feeAmount,
          duration: duration,
          timestamp: new Date(),
          recipient: account
        });
        
        setSuccess(`${feeAmount} WLC 수수료가 성공적으로 출금되었습니다!`);
        
        // 정보 새로고침
        loadContractInfo();
      }, 500);

    } catch (error) {
      console.error('수수료 출금 실패:', error);
      setWithdrawalState('error');
      
      let errorMessage = '수수료 출금에 실패했습니다.';
      
      if (error.code === 4001) {
        errorMessage = '사용자가 트랜잭션을 거부했습니다.';
      } else if (error.message) {
        errorMessage = '수수료 출금에 실패했습니다: ' + error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      
      // 5초 후 상태 초기화
      setTimeout(() => {
        setWithdrawalState('idle');
        setWithdrawalProgress(0);
        setSuccess(null);
        setError(null);
      }, 10000);
    }
  };

  // 결과 요약 모달 닫기
  const closeWithdrawalResult = () => {
    setWithdrawalResult(null);
    setWithdrawalState('idle');
    setWithdrawalProgress(0);
  };

  // 블록 탐색기에서 보기
  const viewOnExplorer = (address) => {
    window.open(`https://scan.worldland.foundation/address/${address}`, '_blank');
  };

  // 소유권 이전 (기존 코드)
  const handleTransferOwnership = async () => {
    if (!isOwner) {
      setError('소유권 이전 권한이 없습니다.');
      return;
    }

    if (!newOwnerAddress || newOwnerAddress.length !== 42 || !newOwnerAddress.startsWith('0x')) {
      setError('올바른 지갑 주소를 입력해주세요.');
      return;
    }

    const confirmMessage = `정말로 소유권을 다음 주소로 이전하시겠습니까?\n\n${newOwnerAddress}\n\n⚠️ 주의: 이 작업은 되돌릴 수 없습니다!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await transferOwnership(newOwnerAddress);
      setSuccess('소유권이 성공적으로 이전되었습니다!');
      setNewOwnerAddress('');
      loadContractInfo();
    } catch (error) {
      console.error('소유권 이전 실패:', error);
      if (error.code === 4001) {
        setError('사용자가 트랜잭션을 거부했습니다.');
      } else {
        setError('소유권 이전에 실패했습니다: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 수수료 변경 진행 상태 렌더링
  const renderFeeChangeProgress = () => {
    if (feeSettings.feeChangeState === 'idle') return null;

    const stateConfig = {
      confirming: {
        title: '⚙️ 수수료율 변경 준비',
        description: '수수료율 변경 트랜잭션을 준비하고 있습니다...',
        color: '#ffc107'
      },
      processing: {
        title: '⚡ 블록체인 처리 중',
        description: '새로운 수수료율을 블록체인에 기록하고 있습니다...',
        color: '#17a2b8'
      },
      completed: {
        title: '✅ 수수료율 변경 완료',
        description: '수수료율이 성공적으로 변경되었습니다!',
        color: '#28a745'
      },
      error: {
        title: '❌ 변경 실패',
        description: '수수료율 변경 중 오류가 발생했습니다.',
        color: '#dc3545'
      }
    };

    const config = stateConfig[feeSettings.feeChangeState];

    return (
      <div className="fee-change-progress-overlay">
        <div className="fee-change-progress-modal">
          <div className="progress-header">
            <h4>{config.title}</h4>
            <p>{config.description}</p>
          </div>
          
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${feeSettings.feeChangeProgress}%`,
                  backgroundColor: config.color
                }}
              />
            </div>
            <div className="progress-text">
              {Math.round(feeSettings.feeChangeProgress)}%
            </div>
          </div>
          
          <div className="progress-steps">
            <div className={`progress-step ${feeSettings.feeChangeProgress >= 30 ? 'completed' : 'active'}`}>
              준비
            </div>
            <div className={`progress-step ${feeSettings.feeChangeProgress >= 60 ? 'completed' : feeSettings.feeChangeProgress >= 30 ? 'active' : ''}`}>
              처리
            </div>
            <div className={`progress-step ${feeSettings.feeChangeProgress >= 100 ? 'completed' : feeSettings.feeChangeProgress >= 60 ? 'active' : ''}`}>
              완료
            </div>
          </div>
          
          {feeSettings.feeChangeState === 'processing' && (
            <div className="processing-animation">
              <div className="spinner"></div>
              <p>블록 확인을 기다리는 중...</p>
            </div>
          )}
          
          {feeSettings.feeChangeState === 'completed' && (
            <div className="completion-message">
              <p>🎉 새로운 수수료율: <strong>{feeSettings.newFeePercentage || contractInfo.currentFeePercentage}</strong></p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 진행 상태 렌더링
  const renderWithdrawalProgress = () => {
    if (withdrawalState === 'idle') return null;

    const stateConfig = {
      confirming: {
        title: '🔐 트랜잭션 준비 중',
        description: '수수료 출금 트랜잭션을 준비하고 있습니다...',
        color: '#ffc107'
      },
      processing: {
        title: '⚡ 블록체인 처리 중',
        description: '트랜잭션을 블록체인에 기록하고 있습니다...',
        color: '#17a2b8'
      },
      completed: {
        title: '✅ 출금 완료',
        description: '수수료가 성공적으로 출금되었습니다!',
        color: '#28a745'
      },
      error: {
        title: '❌ 출금 실패',
        description: '수수료 출금 중 오류가 발생했습니다.',
        color: '#dc3545'
      }
    };

    const config = stateConfig[withdrawalState];

    return (
      <div className="withdrawal-progress-overlay">
        <div className="withdrawal-progress-modal">
          <div className="progress-header">
            <h4>{config.title}</h4>
            <p>{config.description}</p>
          </div>
          
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${withdrawalProgress}%`,
                  backgroundColor: config.color
                }}
              />
            </div>
            <div className="progress-text">
              {Math.round(withdrawalProgress)}%
            </div>
          </div>
          
          <div className="progress-steps">
            <div className={`progress-step ${withdrawalProgress >= 20 ? 'completed' : 'active'}`}>
              준비
            </div>
            <div className={`progress-step ${withdrawalProgress >= 40 ? 'completed' : withdrawalProgress >= 20 ? 'active' : ''}`}>
              처리
            </div>
            <div className={`progress-step ${withdrawalProgress >= 100 ? 'completed' : withdrawalProgress >= 40 ? 'active' : ''}`}>
              완료
            </div>
          </div>
          
          {withdrawalState === 'processing' && (
            <div className="processing-animation">
              <div className="spinner"></div>
              <p>블록 확인을 기다리는 중...</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // 출금 결과 요약 모달
  const renderWithdrawalResult = () => {
    if (!withdrawalResult) return null;

    return (
      <div className="withdrawal-result-overlay" onClick={closeWithdrawalResult}>
        <div className="withdrawal-result-modal" onClick={e => e.stopPropagation()}>
          <div className="result-header">
            <h3>🎉 수수료 출금 완료</h3>
            <button className="close-button" onClick={closeWithdrawalResult}>×</button>
          </div>
          
          <div className="result-content">
            <div className="result-summary">
              <div className="amount-display">
                <span className="amount-value">{withdrawalResult.amount}</span>
                <span className="amount-unit">WLC</span>
              </div>
              <p className="amount-description">수수료가 성공적으로 출금되었습니다</p>
            </div>
            
            <div className="result-details">
              <div className="detail-item">
                <span className="detail-label">📅 출금 시간:</span>
                <span className="detail-value">{withdrawalResult.timestamp.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">⏱️ 처리 시간:</span>
                <span className="detail-value">{withdrawalResult.duration}초</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">💰 출금 주소:</span>
                <span className="detail-value address">{withdrawalResult.recipient}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">🔗 컨트랙트:</span>
                <span className="detail-value address">0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a</span>
              </div>
            </div>
          </div>
          
          <div className="result-actions">
            <button 
              className="explorer-button"
              onClick={() => viewOnExplorer('0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a')}
            >
              🔍 블록 탐색기에서 확인
            </button>
            <button 
              className="close-result-button"
              onClick={closeWithdrawalResult}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!account) {
    return (
      <div className="admin-panel">
        <div className="admin-message">
          <h3>관리자 패널</h3>
          <p>지갑을 연결해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>관리자 패널</h2>
        <p>스마트 컨트랙트 관리 및 수수료 출금</p>
      </div>

      <div className="admin-content">
        {/* 컨트랙트 정보 */}
        <div className="info-section">
          <h3>컨트랙트 정보</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">컨트랙트 주소:</span>
              <span className="value address" onClick={() => viewOnExplorer('0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a')}>
                0xc44d3ec358353c3f4639bdfd0f40b936f2c56d0a
                <span className="view-icon">🔍</span>
              </span>
            </div>
            <div className="info-item">
              <span className="label">소유자:</span>
              <span className="value address" onClick={() => viewOnExplorer(contractInfo.owner)}>
                {contractInfo.owner}
                <span className="view-icon">🔍</span>
              </span>
            </div>
            <div className="info-item">
              <span className="label">현재 수수료율:</span>
              <span className="value highlight fee-rate">
                {contractInfo.currentFeePercentage}
                <span className="fee-detail">({contractInfo.currentFeePercent}/10000)</span>
              </span>
            </div>
            <div className="info-item">
              <span className="label">누적 수수료:</span>
              <span className="value highlight">{contractInfo.totalFees} WLC</span>
            </div>
            <div className="info-item">
              <span className="label">컨트랙트 잔액:</span>
              <span className="value">{contractInfo.contractBalance} WLC</span>
            </div>
            <div className="info-item">
              <span className="label">총 거래 수:</span>
              <span className="value">{parseInt(contractInfo.nextTradeId) - 1}개</span>
            </div>
          </div>
        </div>

        {/* 권한 상태 */}
        <div className="permission-section">
          <h3>권한 상태</h3>
          <div className={`permission-status ${isOwner ? 'owner' : 'not-owner'}`}>
            {isOwner ? (
              <>
                <span className="icon">👑</span>
                <span>관리자 권한이 있습니다</span>
              </>
            ) : (
              <>
                <span className="icon">❌</span>
                <span>관리자 권한이 없습니다</span>
              </>
            )}
          </div>
        </div>

        {/* 관리자 기능 */}
        {isOwner && (
          <div className="admin-actions">
            <h3>관리자 기능</h3>
            
            {/* 수수료율 변경 */}
            <div className="action-card">
              <h4>⚙️ 수수료율 관리</h4>
              <p>거래 수수료율을 변경합니다. 변경 후 생성되는 새 거래부터 적용됩니다.</p>
              <div className="fee-current-info">
                <div className="current-fee-display">
                  <span className="label">현재 수수료율:</span>
                  <span className="current-fee-value">{contractInfo.currentFeePercentage}</span>
                  <span className="fee-explanation">(거래 완료 시 차감)</span>
                </div>
              </div>
              <div className="fee-change-section">
                <div className="fee-input-group">
                  <label htmlFor="newFeePercentage">새 수수료율 (%)</label>
                  <input
                    type="number"
                    id="newFeePercentage"
                    min="0"
                    max="10"
                    step="0.01"
                    value={feeSettings.newFeePercentage}
                    onChange={handleFeePercentageChange}
                    placeholder="예: 1.5"
                    className="fee-input"
                    disabled={loading || feeSettings.isChangingFee}
                  />
                  <span className="fee-input-hint">0% ~ 10% 범위에서 설정 가능</span>
                </div>
                {feeSettings.newFeePercentage && (
                  <div className="fee-preview">
                    <div className="preview-item">
                      <span>내부 값:</span>
                      <span>{feeSettings.newFeePercent}/10000</span>
                    </div>
                    <div className="preview-item">
                      <span>실제 수수료:</span>
                      <span>{feeSettings.newFeePercentage}%</span>
                    </div>
                    <div className="preview-example">
                      <strong>예시:</strong> 1000 WLC 거래 시 {(1000 * parseFloat(feeSettings.newFeePercentage || 0) / 100).toFixed(2)} WLC 수수료
                    </div>
                  </div>
                )}
                <button 
                  className={`action-button primary ${feeSettings.feeChangeState !== 'idle' ? 'processing' : ''}`}
                  onClick={handleChangeFeePercent}
                  disabled={loading || !feeSettings.newFeePercentage || feeSettings.isChangingFee}
                >
                  {feeSettings.feeChangeState === 'idle' ? (
                    loading ? '처리 중...' : '⚙️ 수수료율 변경'
                  ) : feeSettings.feeChangeState === 'confirming' ? (
                    '🔐 확인 중...'
                  ) : feeSettings.feeChangeState === 'processing' ? (
                    '⚡ 처리 중...'
                  ) : feeSettings.feeChangeState === 'completed' ? (
                    '✅ 변경 완료'
                  ) : (
                    '❌ 변경 실패'
                  )}
                </button>
              </div>
            </div>
            
            {/* 수수료 출금 */}
            <div className="action-card">
              <h4>수수료 출금</h4>
              <p>누적된 거래 수수료를 출금합니다.</p>
              <div className="fee-info">
                <span>출금 가능한 수수료: <strong>{contractInfo.totalFees} WLC</strong></span>
              </div>
              <button 
                className={`action-button primary ${withdrawalState !== 'idle' ? 'processing' : ''}`}
                onClick={handleWithdrawFees}
                disabled={loading || parseFloat(contractInfo.totalFees) === 0 || withdrawalState !== 'idle'}
              >
                {withdrawalState === 'idle' ? (
                  loading ? '처리 중...' : '💰 수수료 출금하기'
                ) : withdrawalState === 'confirming' ? (
                  '🔐 확인 중...'
                ) : withdrawalState === 'processing' ? (
                  '⚡ 처리 중...'
                ) : withdrawalState === 'completed' ? (
                  '✅ 출금 완료'
                ) : (
                  '❌ 출금 실패'
                )}
              </button>
            </div>

            {/* 소유권 이전 */}
            <div className="action-card">
              <h4>소유권 이전</h4>
              <p>컨트랙트 소유권을 다른 주소로 이전합니다.</p>
              <div className="ownership-transfer">
                <input
                  type="text"
                  placeholder="새 소유자 지갑 주소 (0x...)"
                  value={newOwnerAddress}
                  onChange={(e) => setNewOwnerAddress(e.target.value)}
                  className="address-input"
                  disabled={loading}
                />
                <button 
                  className="action-button danger"
                  onClick={handleTransferOwnership}
                  disabled={loading || !newOwnerAddress}
                >
                  {loading ? '처리 중...' : '🔄 소유권 이전'}
                </button>
              </div>
              <small className="warning-text">
                ⚠️ 주의: 소유권 이전은 되돌릴 수 없습니다!
              </small>
            </div>

            {/* 새로고침 */}
            <div className="action-card">
              <h4>정보 새로고침</h4>
              <p>최신 컨트랙트 정보를 불러옵니다.</p>
              <button 
                className="action-button secondary"
                onClick={loadContractInfo}
                disabled={loading}
              >
                🔄 새로고침
              </button>
            </div>
          </div>
        )}

        {/* 메시지 표시 */}
        {error && withdrawalState === 'idle' && (
          <div className="message error">
            {error}
          </div>
        )}

        {success && withdrawalState === 'idle' && (
          <div className="message success">
            {success}
          </div>
        )}

        {/* 수수료 정보 */}
        <div className="fee-info-section">
          <h3>수수료 시스템 정보</h3>
          <div className="fee-explanation">
            <div className="fee-info-grid">
              <div className="fee-info-item">
                <h4>🔄 수수료 적용 방식</h4>
                <ul>
                  <li>현재 수수료율: <strong>{contractInfo.currentFeePercentage}</strong></li>
                  <li>거래 생성 시점의 수수료율이 해당 거래에 고정 적용</li>
                  <li>거래 완료 시 WLC에서 수수료가 자동 차감</li>
                  <li>차감된 수수료는 컨트랙트에 누적 저장</li>
                </ul>
              </div>
              <div className="fee-info-item">
                <h4>💡 수수료율 변경 정책</h4>
                <ul>
                  <li>관리자만 수수료율 변경 가능</li>
                  <li>최대 10%까지 설정 가능 (남용 방지)</li>
                  <li>기존 진행 중인 거래는 기존 수수료율 유지</li>
                  <li>변경 후 생성되는 새 거래부터 새 수수료율 적용</li>
                </ul>
              </div>
              <div className="fee-info-item">
                <h4>💰 수수료 사용 용도</h4>
                <ul>
                  <li>플랫폼 운영 및 서버 유지비</li>
                  <li>스마트 컨트랙트 개발 및 업그레이드</li>
                  <li>보안 감사 및 버그 수정</li>
                  <li>커뮤니티 발전 및 마케팅</li>
                </ul>
              </div>
              <div className="fee-info-item">
                <h4>🔒 보안 및 투명성</h4>
                <ul>
                  <li>모든 수수료 변경은 블록체인에 기록</li>
                  <li>수수료 출금 내역도 투명하게 공개</li>
                  <li>커뮤니티에서 수수료 정책 변경 제안 가능</li>
                  <li>비정상적인 수수료 인상 시 거버넌스 개입</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 관리자 도움말 */}
        {isOwner && (
          <div className="admin-help-section">
            <h3>관리자 도움말</h3>
            <div className="help-content">
              <details>
                <summary>수수료율은 언제 변경해야 하나요?</summary>
                <p>시장 상황, 운영비 변화, 경쟁력 확보 등을 고려하여 적정 수수료율을 유지하세요. 너무 높으면 사용자 이탈, 너무 낮으면 운영 지속성에 문제가 될 수 있습니다. 일반적으로 0.5% ~ 2% 범위가 적정합니다.</p>
              </details>
              <details>
                <summary>수수료율 변경이 기존 거래에 미치는 영향</summary>
                <p>수수료율 변경은 변경 후 생성되는 새로운 거래에만 적용됩니다. 이미 생성된 거래는 생성 당시의 수수료율이 그대로 적용되므로 기존 사용자에게 불이익을 주지 않습니다.</p>
              </details>
              <details>
                <summary>수수료 출금은 언제 해야 하나요?</summary>
                <p>수수료는 필요에 따라 언제든 출금할 수 있습니다. 정기적으로 출금하여 플랫폼 운영비용으로 사용하시면 됩니다. 누적 금액이 클 때는 보안을 위해 여러 번에 나누어 출금하는 것을 권장합니다.</p>
              </details>
              <details>
                <summary>소유권 이전 시 주의사항</summary>
                <p>소유권을 이전하면 더 이상 관리자 기능을 사용할 수 없습니다. 신중하게 결정하시고, 새 소유자의 주소를 정확히 확인해주세요. 멀티시그 지갑 사용을 고려해보세요.</p>
              </details>
              <details>
                <summary>블록 탐색기 활용</summary>
                <p>주소를 클릭하면 월드랜드 블록 탐색기에서 상세 정보를 확인할 수 있습니다. 트랜잭션 내역과 잔액을 실시간으로 확인하세요.</p>
              </details>
              <details>
                <summary>환경변수 설정</summary>
                <p>.env 파일에서 REACT_APP_DEFAULT_FEE_PERCENT를 설정하여 기본 수수료율을 변경할 수 있습니다. 개발/테스트 환경에서 유용합니다.</p>
              </details>
            </div>
          </div>
        )}
      </div>

      {/* 진행 상태 오버레이 */}
      {renderWithdrawalProgress()}

      {/* 수수료 변경 진행 상태 오버레이 */}
      {renderFeeChangeProgress()}

      {/* 출금 결과 모달 */}
      {renderWithdrawalResult()}
    </div>
  );
};

export default AdminPanel;